import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  History,
  Check,
  InfoOutlined,
  DescriptionOutlined,
  PhotoLibraryOutlined,
  AccessTimeOutlined,
  Chat,
} from "@mui/icons-material";
import PackageEditLogsDialog from "./PackageEditLogsDialog";
import PackageWhatsAppLogsDialog from "./PackageWhatsAppLogsDialog";
import { useTranslation } from "react-i18next";
import { GetRouteDetail } from "api/public";
import { GetPackageDetailByTracking } from "api/public";
import { GetTrackingLog } from "api/public";
import moment from "moment";
import PhotoPreviewDialog from "./PhotoPreviewDialog";
import { getPhotoUrl } from "utils";
import {
  Inventory,
  LocalShipping,
  Storefront,
  CheckCircle as CheckCircleIcon,
  Cancel,
  AssignmentReturn,
  HourglassEmpty,
  ErrorOutline,
  HelpOutline,
} from "@mui/icons-material";
import {
  MONO,
  CARD_SHADOW,
  KPI_SHADOW_PURPLE,
  PURPLE_LIGHT,
  PURPLE_MAIN,
  PURPLE_DARK,
  PURPLE_DARKER,
  PURPLE_TINT_100,
  TEXT_BODY,
  TEXT_SECONDARY,
  TEXT_SUBTLE,
  BG_WHITE,
  BG_TINT,
  BG_PURPLE_GRADIENT,
  BORDER,
  BORDER_PURPLE,
  DIVIDER,
  RADIUS_CARD,
  RADIUS_TILE,
  RADIUS_PILL,
  cardLabelSx,
} from "components/bento/bentoTokens";
import { useTheme } from "@mui/material/styles";

const TrackingDetail = ({ trackingNumber, masked = false, onReady, hideEditHistory = false }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [currentStation, setCurrentStation] = useState("");
  const [navigator, setNavigator] = useState([]);
  const [trackingLog, setTrackingLog] = useState(null);
  const [packageDetail, setPackageDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [editLogsOpen, setEditLogsOpen] = useState(false);
  const [whatsappLogsOpen, setWhatsappLogsOpen] = useState(false);

  const STATUS_STYLE = {
    started:       { Icon: Inventory,          color: "#78909C" },
    "in-progress": { Icon: LocalShipping,      color: theme.palette.info?.main || "#1976D2" },
    "in-transit":  { Icon: LocalShipping,      color: theme.palette.info?.main || "#1976D2" },
    arrived:       { Icon: Storefront,          color: theme.palette.secondary.main },
    delivered:     { Icon: CheckCircleIcon,    color: theme.palette.success.main },
    cancelled:     { Icon: Cancel,             color: theme.palette.error.main },
    returned:      { Icon: AssignmentReturn,   color: "#E65100" },
    "on hold":     { Icon: HourglassEmpty,     color: "#F57F17" },
    pending:       { Icon: HourglassEmpty,     color: "#F57F17" },
    lost:          { Icon: ErrorOutline,       color: "#B71C1C" },
  };
  const DEFAULT_STYLE = { Icon: HelpOutline, color: theme.palette.grey[400] };

  const stationStatusMap = useMemo(() => {
    const map = {};
    // Navigator provides base mappings (lower priority)
    (navigator || []).forEach(({ station, stationName, status }) => {
      if (stationName) map[stationName] = status?.toLowerCase();
      if (station) map[station] = status?.toLowerCase();
    });
    // Explicit overrides — sort/hub stations stay "in-progress" (truck),
    // only final destination branches map to "arrived" (Storefront)
    Object.assign(map, {
      "STD": "started",     "Pending": "started",
      "NKH": "in-progress", "Nongkhai": "in-progress",
      "VTE": "in-progress", "Vientiane": "in-progress", // sort hub, not pickup branch
      "END": "delivered",   "End": "delivered",
      "SVK": "arrived",     "Savannakhet": "arrived",
      "PHT": "arrived",     "Phontong": "arrived",
      "THK": "arrived",     "ThongKang": "arrived",
      "DKC": "arrived",     "DongKhamChang": "arrived",
    });
    return map;
  }, [navigator]);

  const getStepStyle = (stationName) => {
    const status = (stationStatusMap[stationName] || stationName || "").toLowerCase();
    return STATUS_STYLE[status] || DEFAULT_STYLE;
  };

  // Most recent log date per station, used as the date caption under each
  // progress-stepper stop. trackingLog is newest-first, so first match wins.
  const stationDateMap = useMemo(() => {
    const map = {};
    (trackingLog || []).forEach((log) => {
      if (log.station && !map[log.station]) {
        map[log.station] = moment(log.createdAt).tz("Asia/Vientiane").format("DD/MM/YYYY");
      }
    });
    return map;
  }, [trackingLog]);

  useEffect(() => {
    if (trackingNumber) {
      fetchTrackingDetails(trackingNumber);
    } else {
      // Clear all data if trackingNumber is empty
      setPackageDetail(null);
      setTrackingLog(null);
      setNavigator([]);
      setError(null);
    }
  }, [trackingNumber]);

  const fetchTrackingDetails = async (tracking) => {
    setIsLoading(true);
    setError(null);
    setPackageDetail(null);
    setTrackingLog(null);
    setNavigator([]);

    try {
      const res = await GetPackageDetailByTracking(tracking, masked);

      if (res.status === 200) {
        setPackageDetail(res.data);
        setCurrentStation(res.data.station);

        // Fetch route and tracking logs in parallel
        const [routeRes, trackingLogRes] = await Promise.all([
          GetRouteDetail(res.data.routeId),
          GetTrackingLog(tracking)
        ]);

        if (routeRes && routeRes.status === 200) {
          setNavigator(routeRes.data.navigator || []);
        } else {
          console.error("Route detail fetch failed:", routeRes?.message);
        }

        if (trackingLogRes && trackingLogRes.status === 200) {
          setTrackingLog(trackingLogRes.data);
        } else {
          console.error("Tracking log fetch failed:", trackingLogRes?.message);
        }

      } else if (res.status === 204) {
        setError(`The tracking number "${tracking}" was not found.`);
      } else {
        setError(res.message || "An unexpected error occurred.");
      }
    } catch (err) {
      console.error("Error fetching tracking details:", err);
      setError("Failed to fetch tracking details. Please try again.");
    } finally {
      setIsLoading(false);
      onReady?.();
    }
  };

  // Index of the station the parcel is actually at right now — used to mark
  // stepper stops as done/active/pending (-1 means "unknown", all pending).
  const activeIdx = navigator.findIndex((item) => item.station === currentStation);

  return (
    <Box marginBottom={2}>
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
          <Typography ml={2}>Loading tracking details...</Typography>
        </Box>
      )}

      {error && !isLoading && (
        <Paper
          sx={{
            maxWidth: 800,
            borderRadius: 2,
            p: 4,
            mt: 1,
            backgroundColor: theme.palette.background.alt,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Paper>
      )}

      {!isLoading && !error && packageDetail && (
        <Paper
          sx={{
            maxWidth: 800,
            borderRadius: RADIUS_CARD,
            p: 0,
            mt: 1,
            overflow: "hidden",
            background: BG_WHITE,
            border: `1px solid ${BORDER}`,
            boxShadow: CARD_SHADOW,
          }}
        >
          <Box sx={{ p: "24px 24px 0" }}>
            {/* ══ PROGRESS STEPPER ══ */}
            {navigator.length > 0 && (
              <Box
                sx={{
                  position: "relative",
                  p: "22px 20px 26px",
                  background: BG_PURPLE_GRADIENT,
                  border: `1px solid ${BORDER_PURPLE}`,
                  borderRadius: RADIUS_TILE,
                  mb: "24px",
                  overflowX: "auto",
                }}
              >
                {(() => {
                  const steps = navigator.map(({ station, stationName }, idx) => ({
                    station,
                    stationName,
                    isDone: activeIdx !== -1 && idx < activeIdx,
                    isActive: idx === activeIdx,
                    date: stationDateMap[stationName] || stationDateMap[station],
                  }));
                  return (
                    <>
                      {/* circle row + connectors — siblings in one flex row so the
                          connecting line touches the circle edges exactly */}
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {steps.map(({ station, isDone, isActive }, idx) => (
                          <React.Fragment key={station}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: isActive
                                  ? `linear-gradient(135deg, ${PURPLE_LIGHT}, ${PURPLE_DARKER})`
                                  : isDone
                                    ? PURPLE_TINT_100
                                    : "#f5f4f8",
                                border: isActive
                                  ? `3px solid ${PURPLE_LIGHT}`
                                  : isDone
                                    ? "2px solid #c4b5fd"
                                    : "2px solid #e1dde8",
                                boxShadow: isActive
                                  ? "0 0 0 4px rgba(139,92,246,.18), 0 4px 14px -4px rgba(109,40,217,.5)"
                                  : "none",
                              }}
                            >
                              {isDone && <Check sx={{ fontSize: 16, color: PURPLE_LIGHT }} />}
                              {isActive && <Check sx={{ fontSize: 16, color: "#fff" }} />}
                              {!isDone && !isActive && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#d1c4e9" }} />}
                            </Box>
                            {idx < steps.length - 1 && (
                              <Box
                                sx={{
                                  flex: 1,
                                  height: "3px",
                                  minWidth: 24,
                                  borderRadius: "2px",
                                  background: idx < activeIdx ? `linear-gradient(90deg, ${PURPLE_MAIN}, ${PURPLE_LIGHT})` : "#e9e3f5",
                                }}
                              />
                            )}
                          </React.Fragment>
                        ))}
                      </Box>

                      {/* label row — mirrors the circle row's flex structure (fixed 40px
                          anchor + flex:1 gap) so each label centers under its circle even
                          though label text is wider than the circle itself */}
                      <Box sx={{ display: "flex", alignItems: "flex-start", mt: "10px" }}>
                        {steps.map(({ station, stationName, isDone, isActive, date }, idx) => (
                          <React.Fragment key={station}>
                            <Box sx={{ width: 40, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography sx={{ fontSize: "12.5px", fontWeight: isActive ? 800 : isDone ? 600 : 500, color: isActive || isDone ? PURPLE_DARK : TEXT_SUBTLE, whiteSpace: "nowrap" }}>
                                  {stationName}
                                </Typography>
                                {date && (
                                  <Typography sx={{ fontFamily: MONO, fontSize: "10.5px", color: TEXT_SUBTLE, mt: "3px", fontWeight: 500 }}>
                                    {date}
                                  </Typography>
                                )}
                                {isActive && (
                                  <Box component="span" sx={{ display: "inline-block", fontSize: "10px", fontWeight: 700, color: PURPLE_MAIN, bgcolor: PURPLE_TINT_100, px: "8px", py: "2px", borderRadius: RADIUS_PILL, mt: "4px", letterSpacing: ".04em" }}>
                                    CURRENT
                                  </Box>
                                )}
                              </Box>
                            </Box>
                            {idx < steps.length - 1 && <Box sx={{ flex: 1, minWidth: 24 }} />}
                          </React.Fragment>
                        ))}
                      </Box>
                    </>
                  );
                })()}
              </Box>
            )}

            {/* ══ PACKAGE INFO ══ */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
                gap: "20px",
                mb: "24px",
                alignItems: "start",
              }}
            >
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", mb: "10px" }}>
                  <Typography sx={{ fontFamily: MONO, fontSize: 17, fontWeight: 700, color: PURPLE_DARK }}>
                    {packageDetail.tracking}
                  </Typography>
                  {packageDetail.customer?.name && (
                    <>
                      <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "#d1ccd9" }} />
                      <Typography sx={{ fontSize: "13.5px", color: TEXT_SECONDARY, fontWeight: 600 }}>
                        {packageDetail.customer.name}
                        {packageDetail.customer.whatsapp ? ` · ${packageDetail.customer.whatsapp}` : ""}
                      </Typography>
                    </>
                  )}
                </Box>

                {packageDetail.description && (
                  <Typography sx={{ fontSize: "13.5px", color: TEXT_BODY, lineHeight: 1.65, fontWeight: 500, mb: "10px" }}>
                    {packageDetail.description}
                  </Typography>
                )}

                {packageDetail.remark && (
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: "8px", p: "10px 14px", bgcolor: "#fdf8ff", border: `1px solid ${BORDER_PURPLE}`, borderRadius: "10px", mb: "10px" }}>
                    <InfoOutlined sx={{ fontSize: 14, color: PURPLE_LIGHT, mt: "1px", flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "12.5px", color: PURPLE_MAIN, fontWeight: 600, lineHeight: 1.5 }}>
                      <Box component="span" sx={{ color: TEXT_SUBTLE, fontWeight: 700 }}>
                        Remark:{" "}
                      </Box>
                      {packageDetail.remark}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <DescriptionOutlined sx={{ fontSize: 13, color: TEXT_SUBTLE }} />
                    <Typography sx={{ fontSize: "12px", color: TEXT_SUBTLE, fontWeight: 600 }}>Order#</Typography>
                    <Typography sx={{ fontFamily: MONO, fontSize: "12.5px", color: PURPLE_DARK, fontWeight: 600 }}>
                      {packageDetail.orderId}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <PhotoLibraryOutlined sx={{ fontSize: 13, color: TEXT_SUBTLE }} />
                    <Typography sx={{ fontSize: "12px", color: TEXT_SUBTLE, fontWeight: 600 }}>
                      photos#{packageDetail.photos?.length || 0}
                    </Typography>
                  </Box>
                </Box>

                {Array.isArray(packageDetail.photos) && packageDetail.photos.length > 0 && (
                  <Box sx={{ display: "flex", gap: "10px", mt: "14px", flexWrap: "wrap" }}>
                    {packageDetail.photos.map((photo, idx) => (
                      <Box
                        key={idx}
                        onClick={() => {
                          setPhotoIndex(idx);
                          setPhotoViewerOpen(true);
                        }}
                        sx={{
                          borderRadius: "12px",
                          overflow: "hidden",
                          border: `1.5px solid ${BORDER_PURPLE}`,
                          width: 80,
                          height: 80,
                          bgcolor: PURPLE_TINT_100,
                          position: "relative",
                          cursor: "pointer",
                          flexShrink: 0,
                          "&:hover": { borderColor: PURPLE_LIGHT },
                        }}
                      >
                        <Box
                          component="img"
                          src={getPhotoUrl(photo)}
                          alt={`photo-${idx}`}
                          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                        {typeof photo === "object" && photo.timestamp && (
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background: "linear-gradient(180deg, transparent 0%, rgba(22,13,36,.72) 100%)",
                              px: "6px",
                              py: "4px",
                            }}
                          >
                            <Typography sx={{ fontFamily: MONO, fontSize: "9px", fontWeight: 600, color: "rgba(255,255,255,.92)", lineHeight: 1.3 }}>
                              {moment(photo.timestamp).format("DD/MM/YYYY HH:mm:ss")}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  background: BG_PURPLE_GRADIENT,
                  border: `1.5px solid ${BORDER_PURPLE}`,
                  borderRadius: RADIUS_TILE,
                  p: "18px 22px",
                  textAlign: "center",
                  minWidth: 148,
                  boxShadow: KPI_SHADOW_PURPLE,
                }}
              >
                <Box sx={cardLabelSx}>Shipping Fee</Box>
                <Typography sx={{ fontFamily: MONO, fontSize: 26, fontWeight: 700, color: PURPLE_DARK, letterSpacing: "-.01em", mt: "8px" }}>
                  ฿{(Number(packageDetail.shippingFee || 0) + Number(packageDetail.amount || 0)).toLocaleString()}
                </Typography>
                <Box sx={{ mt: "12px", pt: "12px", borderTop: `1px solid ${BORDER_PURPLE}`, fontSize: "11px", color: TEXT_SUBTLE, fontWeight: 600 }}>
                  THB · {packageDetail.paymentStatus ? "Paid" : "Unpaid"}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ══ STATION HISTORY ══ */}
          {trackingLog && trackingLog.length > 0 && (
            <Box sx={{ borderTop: `1.5px solid ${DIVIDER}`, p: "24px 24px 8px" }}>
              <Box sx={{ ...cardLabelSx, mb: "18px" }}>Station History</Box>
              <Box>
                {trackingLog.map((item, index) => {
                  const { Icon, color: statusColor } = getStepStyle(item.station);
                  const isLatest = index === 0;
                  const hasLine = index < trackingLog.length - 1;
                  return (
                    <Box key={index} sx={{ display: "flex", gap: "18px" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 44 }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "50%",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: isLatest ? statusColor : theme.palette.grey[100],
                            border: isLatest ? `3px solid ${statusColor}` : `2px solid ${theme.palette.grey[300]}`,
                            boxShadow: isLatest ? `0 4px 14px -4px ${statusColor}99` : "none",
                          }}
                        >
                          <Icon sx={{ fontSize: 17, color: isLatest ? "#fff" : theme.palette.grey[500] }} />
                        </Box>
                        {hasLine && (
                          <Box
                            sx={{
                              width: "2px",
                              flex: 1,
                              minHeight: 24,
                              background: `linear-gradient(180deg, ${BORDER_PURPLE} 0%, transparent 100%)`,
                              my: "4px",
                              borderRadius: "2px",
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ flex: 1, pb: "22px", minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <Typography sx={{ fontSize: "15px", fontWeight: 700, color: isLatest ? PURPLE_DARK : TEXT_BODY }}>
                            {item.station}
                          </Typography>
                          {isLatest && (
                            <Box component="span" sx={{ fontSize: "10px", fontWeight: 700, color: PURPLE_MAIN, bgcolor: PURPLE_TINT_100, border: `1px solid ${BORDER_PURPLE}`, px: "8px", py: "2px", borderRadius: RADIUS_PILL }}>
                              LATEST
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: "4px", flexWrap: "wrap" }}>
                          <AccessTimeOutlined sx={{ fontSize: 12, color: "#c3bfcd" }} />
                          <Typography sx={{ fontFamily: MONO, fontSize: "11.5px", color: TEXT_SUBTLE, fontWeight: 500 }}>
                            {moment(item.createdAt).tz("Asia/Vientiane").format("DD/MM/YYYY h:mm:ss A")}
                          </Typography>
                          <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "#d1ccd9" }} />
                          <Typography sx={{ fontSize: "11.5px", color: TEXT_SUBTLE, fontWeight: 600 }}>
                            Action by{" "}
                            <Box component="span" sx={{ color: PURPLE_MAIN, fontWeight: 700 }}>
                              {item.actionByUser}
                            </Box>
                            .
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            mt: "10px",
                            background: isLatest ? "#faf8ff" : "#fafafa",
                            border: `1px solid ${isLatest ? BORDER_PURPLE : BORDER}`,
                            borderRadius: "14px",
                            p: "14px 16px",
                          }}
                        >
                          <Typography sx={{ fontSize: "13px", lineHeight: 1.7, color: TEXT_BODY, fontWeight: 500, m: 0 }}>
                            {item.description}
                          </Typography>
                          {item.remark && (
                            <Typography sx={{ fontSize: "11.5px", lineHeight: 1.6, color: TEXT_SUBTLE, fontStyle: "italic", fontWeight: 500, m: 0, mt: "8px" }}>
                              {item.remark}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {!masked && !hideEditHistory && (
            <Box sx={{ p: "16px 24px", borderTop: `1px solid ${DIVIDER}`, display: "flex", justifyContent: "flex-end", gap: "10px", background: BG_TINT }}>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<Chat sx={{ fontSize: 14 }} />}
                onClick={() => setWhatsappLogsOpen(true)}
                sx={{ borderColor: BORDER_PURPLE, fontWeight: 700, fontSize: "12.5px", borderRadius: "10px", px: "16px" }}
              >
                {t("packageWhatsAppLogs.button")}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<History sx={{ fontSize: 14 }} />}
                onClick={() => setEditLogsOpen(true)}
                sx={{ borderColor: BORDER_PURPLE, fontWeight: 700, fontSize: "12.5px", borderRadius: "10px", px: "16px" }}
              >
                {t("packageEditLogs.button")}
              </Button>
            </Box>
          )}
        </Paper>
      )}

      <PackageEditLogsDialog
        open={editLogsOpen}
        onClose={() => setEditLogsOpen(false)}
        packageId={packageDetail?._id}
        tracking={packageDetail?.tracking}
      />

      <PackageWhatsAppLogsDialog
        open={whatsappLogsOpen}
        onClose={() => setWhatsappLogsOpen(false)}
        packageId={packageDetail?._id}
        tracking={packageDetail?.tracking}
      />

      <PhotoPreviewDialog
        open={photoViewerOpen}
        photos={packageDetail?.photos || []}
        photoIndex={photoIndex}
        onClose={() => setPhotoViewerOpen(false)}
        onPrev={() =>
          setPhotoIndex(
            (photoIndex - 1 + (packageDetail?.photos?.length || 0)) %
            (packageDetail?.photos?.length || 1)
          )
        }
        onNext={() =>
          setPhotoIndex(
            (photoIndex + 1) % (packageDetail?.photos?.length || 1)
          )
        }
      />
    </Box>
  );
};

export default TrackingDetail;
